// Production runs: what is still owed, and what was actually added.
//
// Mounted from AppMain when activeModule === 'productionRuns'.
//
// TWO JOBS, DELIBERATELY ON ONE SCREEN
// ------------------------------------
// A run approved short is a promise to issue materials later. A run that has
// been made is a claim about what it really consumed. Both are loose ends on
// the same object, and splitting them across two screens is how one of them
// gets forgotten — which is exactly what happened to the 16 delivery drops
// that sat open for months.
//
// WHY ACTUALS ARE TYPED, NOT CALCULATED
// -------------------------------------
// The recipe says 159.309 kg of paraffin. The operator poured what the scale
// said. Only one of those numbers is a fact about the batch, and the system
// cannot derive it — somebody carries the printed sheet back and types it in.
// So the form is pre-filled with the planned figure and every box is
// editable: pre-filling saves typing on the lines that matched, and editing
// is the entire point on the lines that did not.

import React, { useCallback, useEffect, useState } from 'react';
import { authedFetch } from './utils/api';
import { color, radius, space } from './ui/theme';
import { Banner, Btn, Card, Chip, ErrorBox, SkeletonCards } from './ui/kit';

async function req(url, opts) {
  const res = await authedFetch(url, opts);
  if (!res.ok) {
    let d = `Request failed (${res.status})`;
    try { const j = await res.json(); d = j.detail?.message || j.detail?.error || j.detail || d; } catch { /* keep */ }
    throw new Error(typeof d === 'string' ? d : 'Request failed');
  }
  return res.json();
}

const inputStyle = {
  width: '100%', padding: '7px 9px', borderRadius: radius.sm, fontSize: 13,
  border: `1px solid ${color.borderStrong}`, fontFamily: 'inherit',
  boxSizing: 'border-box', background: '#fff', color: color.text,
  textAlign: 'right',
};

const num = (v, dp = 3) =>
  Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: dp });

function ActualsForm({ run, onDone, onCancel, notify }) {
  const [detail, setDetail] = useState(null);
  const [values, setValues] = useState({});
  const [produced, setProduced] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const d = await req(`/api/bom/production-runs/${run.production_order_id}`);
        setDetail(d);
        // Pre-filled with the plan: most lines match, and the ones that do
        // not are the only ones worth anybody's attention.
        const seed = {};
        (d.materials || []).forEach((m) => {
          seed[m.raw_material_id] = String(
            m.quantity_consumed != null ? m.quantity_consumed : m.quantity_required);
        });
        setValues(seed);
        setProduced(String(d.order?.quantity_produced ?? d.order?.quantity_planned ?? ''));
      } catch (e) { setErr(e.message); }
    })();
  }, [run.production_order_id]);

  const save = async () => {
    setBusy(true);
    try {
      const actuals = {};
      for (const [k, v] of Object.entries(values)) {
        const n = Number(v);
        if (Number.isNaN(n) || n < 0) {
          throw new Error('Every quantity must be a number of zero or more.');
        }
        actuals[k] = n;
      }
      const r = await req(
        `/api/bom/production-runs/${run.production_order_id}/actuals`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            actuals,
            quantity_produced: produced === '' ? null : Number(produced),
            note: note || null,
          }),
        });
      notify?.(r.detail, 'success');
      onDone?.(r);
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  if (err) return <ErrorBox msg={err} />;
  if (!detail) return <SkeletonCards n={2} />;

  return (
    <Card>
      <div style={{ marginBottom: space(1.5) }}>
        <div style={{ fontWeight: 650, fontSize: 15 }}>
          {detail.order.order_number} — {detail.order.product_name}
        </div>
        <div style={{ fontSize: 12.5, color: color.textSecondary }}>
          Planned {num(detail.order.quantity_planned, 0)} units
          {detail.order.warehouse_name ? ` · ${detail.order.warehouse_name}` : ''}
        </div>
      </div>

      <Banner tone="info" title="Enter what was actually added">
        Stock moves by the <strong>difference</strong> between what you type
        and what has already been issued — more comes out, less goes back.
        Nothing is deducted twice.
      </Banner>

      <div style={{ overflowX: 'auto', marginTop: space(1.5) }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: color.bg }}>
              {['Material', 'Unit', 'Planned', 'Already issued', 'Actual added', 'Variance']
                .map((h) => (
                  <th key={h} style={{
                    padding: space(1), textAlign: h === 'Material' || h === 'Unit' ? 'left' : 'right',
                    borderBottom: `1px solid ${color.border}`, fontWeight: 600,
                  }}>{h}</th>
                ))}
            </tr>
          </thead>
          <tbody>
            {(detail.materials || []).map((m) => {
              const actual = Number(values[m.raw_material_id] ?? 0);
              const variance = actual - Number(m.quantity_required || 0);
              const off = Math.abs(variance) > 1e-9;
              return (
                <tr key={m.raw_material_id}>
                  <td style={{ padding: space(1), borderBottom: `1px solid ${color.border}` }}>
                    {m.material}
                  </td>
                  <td style={{ padding: space(1), borderBottom: `1px solid ${color.border}`, color: color.textSecondary }}>
                    {m.unit || ''}
                  </td>
                  <td style={{ padding: space(1), textAlign: 'right', borderBottom: `1px solid ${color.border}` }}>
                    {num(m.quantity_required)}
                  </td>
                  <td style={{ padding: space(1), textAlign: 'right', borderBottom: `1px solid ${color.border}`, color: color.textSecondary }}>
                    {num(m.deducted_quantity)}
                  </td>
                  <td style={{ padding: space(1), borderBottom: `1px solid ${color.border}`, width: 130 }}>
                    <input
                      style={inputStyle}
                      value={values[m.raw_material_id] ?? ''}
                      inputMode="decimal"
                      onChange={(e) => setValues({ ...values, [m.raw_material_id]: e.target.value })}
                    />
                  </td>
                  <td style={{
                    padding: space(1), textAlign: 'right',
                    borderBottom: `1px solid ${color.border}`,
                    color: off ? (variance > 0 ? color.danger : color.warning) : color.textMuted,
                    fontWeight: off ? 600 : 400,
                  }}>
                    {off ? (variance > 0 ? '+' : '') + num(variance) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: space(2), marginTop: space(2), flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>
            Units actually produced
          </div>
          <input style={{ ...inputStyle, width: 140 }} value={produced}
                 inputMode="decimal"
                 onChange={(e) => setProduced(e.target.value)} />
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>
            Note (why the figures differ, if they do)
          </div>
          <input style={{ ...inputStyle, textAlign: 'left' }} value={note}
                 onChange={(e) => setNote(e.target.value)}
                 placeholder="e.g. spillage during transfer" />
        </div>
      </div>

      <div style={{ marginTop: space(2), display: 'flex', gap: space(1) }}>
        <Btn onClick={save} disabled={busy}>Record actual usage</Btn>
        <Btn variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Btn>
      </div>
    </Card>
  );
}

export default function ProductionRuns({ notify }) {
  const [runs, setRuns] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);
  const [recording, setRecording] = useState(null);

  const load = useCallback(async () => {
    setErr('');
    try { setRuns(await req('/api/bom/production-runs/outstanding')); }
    catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const topUp = async (run) => {
    setBusy(run.production_order_id);
    try {
      const r = await req(
        `/api/bom/production-runs/${run.production_order_id}/top-up`,
        { method: 'POST' });
      notify?.(r.detail, r.fully_issued ? 'success' : 'warning');
      await load();
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  if (err) return <ErrorBox msg={err} />;
  if (!runs) return <SkeletonCards n={3} />;

  if (recording) {
    return (
      <ActualsForm
        run={recording} notify={notify}
        onCancel={() => setRecording(null)}
        onDone={() => { setRecording(null); load(); }}
      />
    );
  }

  return (
    <div>
      {runs.length === 0 ? (
        <Banner tone="success" title="Nothing is owed">
          Every approved run has had its materials issued in full. Runs
          approved short appear here until the missing stock arrives.
        </Banner>
      ) : (
        <Banner tone="warning" title={`${runs.length} run${runs.length === 1 ? '' : 's'} still owed materials`}>
          These were approved before the stock was there. When a delivery
          arrives, issue what is owed from here — it deducts only the
          shortfall, never the whole requirement again.
        </Banner>
      )}

      <div style={{ marginTop: space(2), display: 'grid', gap: space(2) }}>
        {runs.map((run) => (
          <Card key={run.production_order_id}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              gap: space(1), flexWrap: 'wrap', alignItems: 'flex-start',
            }}>
              <div>
                <div style={{ fontWeight: 650, fontSize: 14.5 }}>
                  {run.order_number} — {run.product_name}
                </div>
                <div style={{ fontSize: 12.5, color: color.textSecondary }}>
                  {num(run.quantity, 0)} units
                  {run.warehouse_name ? ` · ${run.warehouse_name}` : ''}
                  {run.approved_by ? ` · approved by ${run.approved_by}` : ''}
                </div>
                {run.approval_reason && (
                  <div style={{
                    marginTop: 6, fontSize: 12.5, color: color.text,
                    background: `${color.warning}14`,
                    borderLeft: `3px solid ${color.warning}`,
                    padding: `${space(0.75)} ${space(1)}`, borderRadius: radius.sm,
                  }}>
                    Approved short: {run.approval_reason}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: space(1) }}>
                <Btn size="sm" onClick={() => topUp(run)}
                     disabled={busy === run.production_order_id}>
                  {run.fully_issuable ? 'Issue what is owed' : 'Issue what has arrived'}
                </Btn>
                <Btn size="sm" variant="secondary"
                     onClick={() => setRecording(run)}>
                  Record actual usage
                </Btn>
              </div>
            </div>

            <div style={{ overflowX: 'auto', marginTop: space(1.5) }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: color.bg }}>
                    {['Material', 'Unit', 'Still owed', 'In stock now', 'Can issue now'].map((h) => (
                      <th key={h} style={{
                        padding: space(1),
                        textAlign: h === 'Material' || h === 'Unit' ? 'left' : 'right',
                        borderBottom: `1px solid ${color.border}`, fontWeight: 600,
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {run.owed.map((o) => {
                    const full = o.can_issue_now >= o.still_owed - 1e-9;
                    return (
                      <tr key={o.raw_material_id}>
                        <td style={{ padding: space(1), borderBottom: `1px solid ${color.border}` }}>{o.material}</td>
                        <td style={{ padding: space(1), borderBottom: `1px solid ${color.border}`, color: color.textSecondary }}>{o.unit || ''}</td>
                        <td style={{ padding: space(1), textAlign: 'right', borderBottom: `1px solid ${color.border}`, fontWeight: 600 }}>{num(o.still_owed)}</td>
                        <td style={{ padding: space(1), textAlign: 'right', borderBottom: `1px solid ${color.border}` }}>{num(o.available_now)}</td>
                        <td style={{
                          padding: space(1), textAlign: 'right',
                          borderBottom: `1px solid ${color.border}`,
                          color: full ? color.success : color.danger, fontWeight: 600,
                        }}>
                          {num(o.can_issue_now)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: space(1) }}>
              <Chip tone={run.fully_issuable ? 'success' : 'warning'}>
                {run.fully_issuable
                  ? 'Everything owed is now in stock'
                  : 'Still short — only part can be issued'}
              </Chip>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
